import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";
import { SERVICES, SAAS } from "@/data/services";

const CROSS_CUTTING = [
    {
        title: "CRM, reporting y operaciones comerciales",
        body: "Integradas en Aceleración comercial y disponibles como capacidad transversal de apoyo en el resto de líneas.",
    },
    {
        title: "Expansión de mercado",
        body: "Forma parte de Aceleración comercial y de Dirección empresarial, según el alcance del proyecto.",
    },
    {
        title: "Automatización y soluciones inteligentes",
        body: "Dentro de Consultoría e implementación de IA y, cuando aporta valor, aplicadas en las demás líneas.",
    },
];

export default function Services() {
    useReveal();
    return (
        <div data-testid="services-page">
            {/* HERO */}
            <section className="container-x pt-16 pb-16 lg:pt-24 lg:pb-20 border-b border-brand-line">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono">Servicios</div>
                        <h1 className="mt-8 font-heading text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tighter text-brand-navy">
                            Cinco líneas de servicio para{" "}
                            <span className="italic text-brand-electric">hacer crecer tu empresa.</span>
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <p className="text-brand-gray text-lg leading-relaxed">
                            Consultoría empresarial, transformación digital y
                            ejecución comercial. Trabajamos con empresas B2B y B2C,
                            con especial foco en pymes industriales y tecnológicas.
                        </p>
                    </div>
                </div>
            </section>

            {/* FIVE SERVICE LINES */}
            <section className="container-x py-16 lg:py-24" data-testid="services-list">
                <div className="border-t border-brand-line">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`services-row-${s.slug}`}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 py-10 border-b border-brand-line group hover:bg-brand-soft transition-colors reveal"
                        >
                            <div className="lg:col-span-2 label-mono">{s.n}</div>
                            <div className="lg:col-span-4">
                                <h2 className="font-heading text-2xl lg:text-3xl text-brand-navy leading-tight">
                                    {s.name}
                                </h2>
                            </div>
                            <div className="lg:col-span-4">
                                <p className="text-brand-gray leading-relaxed">{s.short}</p>
                            </div>
                            <div className="lg:col-span-2 flex lg:justify-end items-center">
                                <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-electric group-hover:text-brand-navy transition-colors">
                                    Explorar servicio
                                    <ArrowUpRight
                                        size={18}
                                        className="opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                                    />
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* CAPACIDADES TRANSVERSALES */}
            <section className="border-t border-brand-line" data-testid="services-crosscutting">
                <div className="container-x py-16 lg:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 reveal">
                        <div className="lg:col-span-4">
                            <div className="label-mono">Capacidades transversales</div>
                            <h2 className="font-heading text-3xl lg:text-4xl mt-6 leading-tight text-brand-navy">
                                Lo que atraviesa todas las líneas.
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-line border border-brand-line reveal">
                        {CROSS_CUTTING.map((c, i) => (
                            <div key={i} className="bg-white p-8" data-testid={`crosscutting-${i}`}>
                                <h3 className="font-heading text-xl text-brand-navy leading-tight">
                                    {c.title}
                                </h3>
                                <p className="mt-4 text-sm text-brand-gray leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SAAS — PRÓXIMAMENTE (independiente de los 5 servicios) */}
            <section className="border-t border-brand-line bg-brand-soft" data-testid="services-saas">
                <div className="container-x py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-8 reveal">
                        <div className="flex items-center gap-4">
                            <div className="label-mono">Producto</div>
                            <span className="label-mono !text-[10px] border border-brand-electric !text-brand-electric px-2.5 py-1">
                                {SAAS.badge}
                            </span>
                        </div>
                        <h2 className="font-heading text-3xl lg:text-4xl mt-4 text-brand-navy">
                            {SAAS.name}
                        </h2>
                        <p className="mt-3 text-brand-gray max-w-xl leading-relaxed">
                            {SAAS.text}
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right reveal">
                        <Link to="/saas" data-testid="services-saas-link" className="btn-secondary">
                            Más información <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="border-t border-brand-line">
                <div className="container-x py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end reveal">
                    <div className="lg:col-span-8">
                        <div className="label-mono">Colaboremos</div>
                        <h2 className="font-heading text-4xl lg:text-6xl mt-6 leading-[0.98] text-brand-navy">
                            Cuéntanos qué quieres lograr y{" "}
                            <span className="italic text-brand-electric">diseñamos el siguiente paso.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right flex lg:justify-end gap-4 flex-wrap">
                        <Link to="/assessment" data-testid="services-cta-assessment" className="btn-ghost">
                            Hacer el diagnóstico
                        </Link>
                        <Link to="/contact" data-testid="services-cta-contact" className="btn-primary">
                            Agenda una reunión <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
